import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";

const FIELDS = [
  { name: "name", label: "Nama lengkap", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "phone", label: "No. HP / WhatsApp", required: true },
  { name: "birth_date", label: "Tanggal lahir", type: "date" },
  { name: "school", label: "Asal sekolah / instansi" },
  { name: "target", label: "Target (mis. Bintara POLRI)" },
  { name: "password", label: "Kata sandi (min. 6 karakter)", type: "password", required: true },
];

function PackagePicker({ packages, value, onChange }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="register-package-picker">
      {packages.map((p) => (
        <button
          type="button"
          key={p.id}
          onClick={() => onChange(p.id)}
          className={`text-left rounded-lg border p-4 transition-colors duration-200 ${value === p.id ? "border-amber-500 bg-amber-50 ring-1 ring-amber-500" : "border-slate-200 hover:border-slate-400"}`}
          data-testid={`register-package-option-${p.code}`}
        >
          <span className="text-[10px] uppercase tracking-widest text-amber-600 font-semibold">{p.category}</span>
          <span className="block font-display font-semibold text-slate-900 mt-1">{p.name}</span>
          <span className="block text-sm text-slate-600 mt-1">{rupiah(p.price)} · {p.duration_days} hari</span>
        </button>
      ))}
    </div>
  );
}

function LoggedInOrder({ packages, packageId, setPackageId }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const order = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/student/orders", { package_id: packageId });
      toast.success(`Tagihan ${data.invoice} dibuat`);
      nav(`/pembayaran?invoice=${data.invoice}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-6">
      <p className="text-slate-600">Anda sudah masuk. Pilih paket untuk membuat tagihan baru.</p>
      <PackagePicker packages={packages} value={packageId} onChange={setPackageId} />
      <Button onClick={order} disabled={!packageId || busy} className="bg-slate-900" data-testid="order-package-button">
        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Buat Tagihan
      </Button>
    </div>
  );
}

export default function Register() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user, setUser } = useAuth();
  const { data: packages } = useApi("/packages");
  const [packageId, setPackageId] = useState(params.get("paket") || "");
  const [form, setForm] = useState({ study_mode: "online", address: "" });
  const [busy, setBusy] = useState(false);
  const selected = (packages || []).find((p) => p.id === packageId);

  const submit = async (e) => {
    e.preventDefault();
    if (!packageId) return toast.error("Pilih paket terlebih dahulu");
    setBusy(true);
    try {
      const { data } = await api.post("/register", { ...form, package_id: packageId });
      setUser(data.user);
      toast.success("Pendaftaran berhasil! Silakan lakukan pembayaran.");
      nav(`/pembayaran?invoice=${data.payment.invoice}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20" data-testid="register-page">
      <p className="eyebrow">Pendaftaran Siswa</p>
      <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">Daftar & pilih paket</h1>
      <div className="mt-12 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          {user?.role === "siswa" ? (
            <LoggedInOrder packages={packages || []} packageId={packageId} setPackageId={setPackageId} />
          ) : user ? (
            <p className="text-slate-600">Anda masuk sebagai {user.role}. Pendaftaran hanya untuk calon siswa.</p>
          ) : (
            <form onSubmit={submit} className="space-y-10" data-testid="register-form">
              <section>
                <h2 className="font-display text-lg font-semibold mb-4"><span className="font-mono text-amber-600 mr-2">01</span>Pilih paket</h2>
                <PackagePicker packages={packages || []} value={packageId} onChange={setPackageId} />
              </section>
              <section>
                <h2 className="font-display text-lg font-semibold mb-4"><span className="font-mono text-amber-600 mr-2">02</span>Data diri</h2>
                <div className="grid sm:grid-cols-2 gap-5">
                  {FIELDS.map((f) => (
                    <div key={f.name} className="space-y-2">
                      <Label htmlFor={f.name}>{f.label}</Label>
                      <Input id={f.name} type={f.type || "text"} required={f.required} value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`register-${f.name.replace("_", "-")}-input`} />
                    </div>
                  ))}
                  <div className="space-y-2">
                    <Label htmlFor="study_mode">Mode belajar</Label>
                    <select id="study_mode" className="h-9 w-full rounded-md border border-input bg-white px-3 text-sm" value={form.study_mode} onChange={(e) => setForm({ ...form, study_mode: e.target.value })} data-testid="register-study-mode-select">
                      <option value="online">Online</option>
                      <option value="offline">Tatap muka</option>
                    </select>
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="address">Alamat</Label>
                    <Textarea id="address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="register-address-input" />
                  </div>
                </div>
              </section>
              <p className="text-xs text-slate-500">Dengan mendaftar, Anda menyetujui <Link to="/syarat-layanan" className="underline">Syarat Layanan</Link> dan <Link to="/kebijakan-privasi" className="underline">Kebijakan Privasi</Link>.</p>
              <Button type="submit" size="lg" disabled={busy} className="bg-amber-500 text-slate-900 hover:bg-amber-400 font-semibold" data-testid="register-submit-button">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Daftar & Lanjut Pembayaran
              </Button>
            </form>
          )}
        </div>
        <aside className="lg:col-span-4">
          <div className="sticky top-24 rounded-lg bg-slate-900 text-white p-6" data-testid="register-summary">
            <p className="text-xs uppercase tracking-widest text-amber-400">Ringkasan</p>
            {selected ? (
              <>
                <p className="font-display text-xl font-bold mt-3">{selected.name}</p>
                <p className="text-sm text-slate-400 mt-1">Masa aktif {selected.duration_days} hari</p>
                <p className="font-display text-3xl font-extrabold mt-6">{rupiah(selected.price)}</p>
              </>
            ) : <p className="text-sm text-slate-400 mt-3">Belum ada paket dipilih.</p>}
            <p className="text-xs text-slate-400 mt-6 leading-relaxed">Setelah mendaftar, Anda akan mendapat nomor invoice. Transfer sesuai nominal dan unggah bukti pembayaran untuk diverifikasi admin.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
